import styled from 'styled-components';

// Filter Overlay for mobile
export const FilterOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.show ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Filter Dropdown Container
export const FilterDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.primary || '#1a1a2e'
      : props.theme.card?.background || 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.theme.card?.shadowHover || 'rgba(0, 0, 0, 0.15) 0px 4px 12px'};
  z-index: 1000;

  /* Inner content wrapper */
  & > div:first-child {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.1)' : 'transparent'};
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: 8px;
  }

  @media (max-width: 768px) {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    max-height: 80vh;
    border-radius: 16px 16px 0 0;
    z-index: 10000 !important;
    transform: none !important;
    margin: 0 !important;
    box-sizing: border-box;
    overflow: hidden;

    & > div:first-child {
      padding: 20px 16px;
      max-height: 80vh;
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    }
  }

  /* Custom scrollbar */
  & > div:first-child::-webkit-scrollbar {
    width: 8px;
  }

  & > div:first-child::-webkit-scrollbar-track {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f1f1'};
    border-radius: 8px;
  }

  & > div:first-child::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.2)' : '#888'};
    border-radius: 8px;
  }

  & > div:first-child::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.3)' : '#555'};
  }
`;

export const FilterSection = styled.div`
  margin-bottom: 20px;

  &:last-of-type {
    margin-bottom: 16px;
  }
`;

export const FilterTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: none;
  color: ${(props) => (props.theme.name === 'dark' ? '#e2e8f0' : '#334155')};
  margin-bottom: 12px;
`;

export const FilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.1)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  font-size: 14px;

  @media (hover: hover) {
    &:hover {
      background: ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255,255,255,0.1)'
          : 'rgba(250, 70, 22, 0.05)'};
      border-color: rgba(250, 70, 22, 0.4);
    }
  }

  ${(props) =>
    props.selected &&
    `
    background: ${
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.2)'
        : 'rgba(250, 70, 22, 0.1)'
    };
    border-color: ${props.theme?.colors?.primary || '#FA4616'};
  `}
`;

export const FilterCheckbox = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid
    ${(props) =>
      props.checked
        ? props.theme?.colors?.primary || '#FA4616'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : '#d1d5db'};
  border-radius: 4px;
  transition: all 0.15s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(props) =>
    props.checked ? props.theme?.colors?.primary || '#FA4616' : 'transparent'};

  &::after {
    content: ${(props) => (props.checked ? "'✓'" : "''")};
    color: white;
    font-size: 10px;
    font-weight: bold;
  }
`;

// Radio button styled as checkbox for single selection
export const FilterRadio = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid
    ${(props) =>
      props.checked
        ? props.theme?.colors?.primary || '#FA4616'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : '#d1d5db'};
  border-radius: 50%;
  transition: all 0.15s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) =>
      props.checked
        ? props.theme?.colors?.primary || '#FA4616'
        : 'transparent'};
    transition: all 0.15s ease;
  }
`;

export const FilterActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : 'transparent'};
`;

export const FilterButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: ${props.theme?.colors?.primary || '#fa4616'};
    color: #ffffff;

    &:hover {
      background: #e63e13;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
    }
  `
      : `
    background: ${
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : 'white'
    };
    border: 1px solid ${
      props.theme.name === 'dark'
        ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
        : '#e5e7eb'
    };
    color: ${
      props.theme.name === 'dark'
        ? props.theme?.colors?.primary || '#FA4616'
        : props.theme?.colors?.blueBlack || '#000033'
    };

    &:hover {
      background: ${
        props.theme.name === 'dark' ? 'rgba(255,255,255,0.12)' : '#f9fafb'
      };
      border-color: ${props.theme?.colors?.primary || '#fa4616'};
    }
  `}
`;

// Filter Select (Dropdown)
export const FilterSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#334155')};
  background-color: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.3)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
        : 'rgba(0,0,0,0.15)'};
  border-radius: 6px;
  transition: all 0.15s ease;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark' ? 'none' : '0 0 0 3px rgba(250,70,22,0.1)'};
  }

  option {
    background: ${(props) =>
      props.theme.name === 'dark' ? '#1F2937' : '#ffffff'};
    color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  }
`;

// Header wrapper for filter button positioning
export const FilterWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Filter icon button
export const FilterIconButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme?.colors?.textGray?.secondary || '#94a3b8'
      : props.theme?.colors?.textGray?.secondary || '#94A3B8'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250,70,22,0.05)'};
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Active state indicator */
  ${(props) =>
    props.hasActiveFilters &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 6px;
      right: 6px;
      width: 8px;
      height: 8px;
      background: ${props.theme?.colors?.primary || '#FA4616'};
      border-radius: 50%;
    }
  `}
`;
